/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { ref, push, onValue, set } from 'firebase/database';
import { database, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { aiChat, AIError } from '../../lib/aiApi';
import { MENTOR_SYSTEM_PREAMBLE } from '../../lib/mentorPersona';
import { formatStructuredEntry, buildMentorContext } from '../../lib/mentorContext';
import {
  MonthRibbon,
  AIChatPanel,
  useFormattingTools
} from './';
import StructuredJournalForms from './StructuredJournalForms';
import MentorCard from '../Mentor/MentorCard';
import '../../styles/GlassEditor.css';

const GlassEditor = ({ showCalendar = true, leftSidebar = null, leftSidebarExpanded = true, externalEntry = null, userGoal = null, businessPlan = null }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [user, setUser] = useState(null);
  const [savedJournals, setSavedJournals] = useState({});
  const [structuredEntries, setStructuredEntries] = useState({});
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [viewMode, setViewMode] = useState('structured'); // 'structured' or 'freeform'
  const [reflection, setReflection] = useState(null); // {status:'pending'|'error', entryId, entryData}
  const [lastFailedInput, setLastFailedInput] = useState(null);
  const chatEndRef = useRef(null);

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Load saved journals and structured entries
  useEffect(() => {
    if (!user) return;

    const journalsRef = ref(database, `journals/${user.uid}`);
    const journalsUnsubscribe = onValue(journalsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setSavedJournals(data);
    });

    const structuredRef = ref(database, `structuredEntries/${user.uid}`);
    const structuredUnsubscribe = onValue(structuredRef, (snapshot) => {
      const data = snapshot.val() || {};
      setStructuredEntries(data);
    });

    return () => {
      journalsUnsubscribe();
      structuredUnsubscribe();
    };
  }, [user]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Save structured journal entry, then request a mentor reflection.
  const saveStructuredEntry = async (entryData) => {
    if (!user) return;

    try {
      const structuredRef = ref(database, `structuredEntries/${user.uid}`);
      const newRef = await push(structuredRef, entryData);
      console.log('Structured entry saved successfully');
      // Fire-and-forget: the save UI never awaits the reflection, so a save always
      // succeeds even when the reflection fails.
      requestEntryReflection(newRef.key, entryData);
    } catch (error) {
      console.error('Error saving structured entry:', error);
    }
  };

  // The witness: generate an honest mentor reflection for a just-saved entry and
  // persist it to the new sibling node users/{uid}/insights/{entryId}. On failure the
  // reflection surfaces an honest, retryable error MentorCard — never canned content.
  const requestEntryReflection = async (entryId, entryData) => {
    setReflection({ status: 'pending', entryId, entryData });
    try {
      const mentorCtx = await buildMentorContext(user.uid, { goal: userGoal, businessPlan, entries: structuredEntries });
      const data = await aiChat({
        task: 'coach-analysis',
        response_format: 'json',
        temperature: 0.6,
        max_tokens: 500,
        messages: [
          {
            role: 'system',
            content: `${MENTOR_SYSTEM_PREAMBLE}\n\n${mentorCtx}\n\nThe user just saved a journal entry. Return ONLY a JSON object {"reveals": string (what this entry reveals about their trajectory, 1-2 sentences), "reframe": string (one honest reframe grounded in their plan), "nextAction": string (one concrete next step)}.`
          },
          {
            role: 'user',
            content: formatStructuredEntry(entryData) || entryData.summary || ''
          }
        ]
      });
      const parsed = JSON.parse((data.choices[0].message.content || '').replace(/^```(json)?|```$/g, '').trim());
      await set(ref(database, `users/${user.uid}/insights/${entryId}`), {
        createdAt: Date.now(),
        planId: businessPlan?.id || null,
        goalId: userGoal?.id || null,
        source: 'entry-reflection',
        reveals: String(parsed.reveals || ''),
        reframe: String(parsed.reframe || ''),
        nextAction: String(parsed.nextAction || '')
      });
      setReflection(null);
    } catch (e) {
      setReflection({ status: 'error', entryId, entryData });
    }
  };

  // Format AI response for better readability
  const formatAIResponse = (text) => {
    return text
      .replace(/\*\*(\d+)\.\s*/g, '\n\n**$1.** ')
      .replace(/\*\*-\s*/g, '\n\n• ')
      .replace(/^-\s*/gm, '• ')
      .replace(/\?\s*\*\*/g, '?\n\n**')
      .replace(/\.\s+([A-Z])/g, '.\n\n$1')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Helper function to format all journals for AI context
  const formatAllJournalsForAI = () => {
    const regularJournals = Object.keys(savedJournals).length > 0 
      ? Object.entries(savedJournals)
          .map(([id, journal]) => ({ id, ...journal, type: 'freeform' }))
      : [];

    const structuredJournalsFlattened = Object.keys(structuredEntries).length > 0
      ? Object.entries(structuredEntries)
          .map(([id, entry]) => ({ 
            id, 
            ...entry, 
            type: 'structured',
            content: formatStructuredEntry(entry)
          }))
      : [];

    const allEntries = [...regularJournals, ...structuredJournalsFlattened]
      .sort((a, b) => new Date(a.createdAt || a.date) - new Date(b.createdAt || b.date))
      .slice(-10);

    if (allEntries.length === 0) {
      return "No previous journal entries available.";
    }

    const journalEntries = allEntries.map(entry => {
      const date = new Date(entry.createdAt || entry.date).toLocaleDateString();
      const content = entry.content.length > 500 
        ? entry.content.substring(0, 500) + '...'
        : entry.content;
      return `**${date} - ${entry.title}:**\n${content}\n`;
    }).join('\n');

    return `**Previous Journal Entries (Last 10):**\n\n${journalEntries}`;
  };

  // Get current entry context for AI
  const getCurrentEntryContext = () => {
    // Since we're using structured forms, we'll get this from the current form state
    // This would need to be passed from the StructuredJournalForms component
    return "Currently working on structured journal entry.";
  };

  // Immediate Business Coaching Analysis
  const getImmediateCoaching = async () => {
    try {
      const allJournalsContext = formatAllJournalsForAI();
      const currentEntryContext = getCurrentEntryContext();
      const mentorCtx = await buildMentorContext(user.uid, { goal: userGoal, businessPlan, entries: structuredEntries });

      const data = await aiChat({
        task: 'coach-analysis',
        messages: [
            {
              role: "system",
              content: `${MENTOR_SYSTEM_PREAMBLE}\n\n${mentorCtx}\n\nYou are an expert Business Coach providing immediate, actionable coaching based on the user's journal entries and professional journey. Analyze their patterns and provide strategic coaching.

              ${currentEntryContext}

              ${allJournalsContext}

              Your role is to:
              1. **Analyze patterns** from their structured journal entries
              2. **Provide specific coaching** on their goals, productivity, and business growth
              3. **Identify opportunities** for improvement and optimization
              4. **Give actionable next steps** they can implement
              5. **Ask strategic questions** to drive deeper thinking

              Focus on their structured data to provide insights about:
              - Goal achievement patterns
              - Productivity optimization
              - Strategic decision-making
              - Leadership development
              - Business growth opportunities

              Format with clear sections:
              - **Pattern Analysis** from their journal history
              - **Strategic Insights** and recommendations
              - **Action Items** for immediate implementation
              - **Key Questions** for reflection`
            },
            {
              role: "user",
              content: `Analyze my journal patterns and provide strategic business coaching. Focus on trends in my goal-setting, productivity, challenges, and growth areas.`
            }
        ],
        temperature: 0.7,
        max_tokens: 600
      });

      if (data.choices && data.choices[0]) {
        return formatAIResponse(data.choices[0].message.content);
      }
    } catch (error) {
      console.error('Error getting immediate coaching:', error);
      // Honest failure: return null so the caller surfaces a real error instead of
      // fabricated coaching. Never invent coaching content.
      return null;
    }

    return null;
  };

  // AI Chat function. Accepts an optional overrideText: a string is the exact text to
  // send (used by retry); any non-string arg (e.g. the SyntheticEvent from AIChatPanel's
  // onClick, or a key event) falls back to the current chatInput. The typeof-string guard
  // is REQUIRED because AIChatPanel invokes it as onClick={sendChatMessage}.
  const sendChatMessage = async (overrideText = null) => {
    const content = typeof overrideText === 'string' ? overrideText : chatInput;
    if (!content.trim()) return;

    const userMessage = { role: 'user', content };
    setChatMessages(prev => [...prev, userMessage]);
    if (typeof overrideText !== 'string') setChatInput('');
    setIsTyping(true);

    try {
      const allJournalsContext = formatAllJournalsForAI();
      const currentEntryContext = getCurrentEntryContext();
      const mentorCtx = await buildMentorContext(user.uid, { goal: userGoal, businessPlan, entries: structuredEntries });

      const data = await aiChat({
        task: 'coach-chat',
        messages: [
            {
              role: "system",
              content: `${MENTOR_SYSTEM_PREAMBLE}\n\n${mentorCtx}\n\nYou are an expert Business Coach with access to the user's complete journal history including structured entries. Continue coaching them based on their ongoing conversation and journey.

              ${currentEntryContext}

              ${allJournalsContext}

              Maintain your role as their personal business coach. Be insightful, direct, and provide actionable coaching that builds on previous conversations. Reference their journal patterns when relevant to show continuity in your coaching relationship.`
            },
            ...chatMessages.filter(m => m.role === 'user' || m.role === 'assistant'),
            userMessage
        ],
        temperature: 0.7,
        max_tokens: 500
      });

      if (data.choices && data.choices[0]) {
        const formattedContent = formatAIResponse(data.choices[0].message.content);
        const aiMessage = { role: 'assistant', content: formattedContent };
        // Drop any lingering error bubble and append the reply. The failed user turn
        // lived ON the error bubble, so no user turn is ever duplicated.
        setChatMessages(prev => [...prev.filter(m => m.role !== 'error'), aiMessage]);
      }
    } catch (e) {
      console.error('Error in AI chat:', e);
      setLastFailedInput(content);
      // Replace the just-appended user bubble (located by reference identity) with a
      // single error bubble that CARRIES the failed text, so retry can never duplicate
      // the user turn in state or in the API payload.
      setChatMessages(prev => {
        const idx = prev.lastIndexOf(userMessage);
        const base = idx >= 0 ? [...prev.slice(0, idx), ...prev.slice(idx + 1)] : [...prev];
        return [...base, {
          role: 'error',
          content,
          errorText: (e instanceof AIError ? e.message : "The mentor couldn't respond — try again.")
        }];
      });
    } finally {
      setIsTyping(false);
    }
  };

  // Handle Business Coach toggle - immediate coaching
  const handleBusinessCoachToggle = async () => {
    if (!showAIChat) {
      setShowAIChat(true);
      setIsTyping(true);
      
      // Get immediate coaching analysis
      const coachingAnalysis = await getImmediateCoaching();

      if (coachingAnalysis === null) {
        // Honest failure — no errorText field, so AIChatPanel renders no Retry button.
        setChatMessages([{ role: 'error', content: "Your mentor couldn't reflect on your journal just now." }]);
        setIsTyping(false);
        setHasShownWelcome(true);
        return;
      }

      const coachingMessage = {
        role: 'assistant',
        content: `**🎯 Your Business Coach is Ready**\n\n${coachingAnalysis}\n\n---\n\n**What would you like to explore further?** Ask me about your productivity patterns, goal achievement strategies, or any business challenge you're facing.`
      };

      setChatMessages([coachingMessage]);
      setIsTyping(false);
      setHasShownWelcome(true);
    } else {
      setShowAIChat(false);
      setChatMessages([]);
      setHasShownWelcome(false);
    }
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // Get all entries for month display (both regular and structured)
  const getAllEntriesForMonth = (monthIndex) => {
    const year = currentDate.getFullYear();
    
    const regularEntries = Object.values(savedJournals).filter(journal => {
      const journalDate = new Date(journal.createdAt);
      return journalDate.getMonth() === monthIndex && journalDate.getFullYear() === year;
    });

    const structuredEntriesForMonth = Object.values(structuredEntries).filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate.getMonth() === monthIndex && entryDate.getFullYear() === year;
    });

    return [...regularEntries, ...structuredEntriesForMonth];
  };

  // Get structured entries for month organized by date
  const getEntriesForMonthByDate = (monthIndex) => {
    const year = currentDate.getFullYear();
    
    const allEntries = [
      ...Object.entries(savedJournals).map(([id, journal]) => ({ 
        id, 
        ...journal, 
        type: 'freeform',
        icon: '📝'
      })),
      ...Object.entries(structuredEntries).map(([id, entry]) => ({ 
        id, 
        ...entry, 
        type: 'structured',
        icon: entry.primaryType === 'daily' ? '☀️' : entry.primaryType === 'meeting' ? '🤝' : '🚀'
      }))
    ]
    .filter(entry => {
      const entryDate = new Date(entry.createdAt || entry.date);
      return entryDate.getMonth() === monthIndex && entryDate.getFullYear() === year;
    })
    .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
    
    // Group by date
    const groupedByDate = allEntries.reduce((acc, entry) => {
      const date = new Date(entry.createdAt || entry.date).toLocaleDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(entry);
      return acc;
    }, {});
    
    return groupedByDate;
  };

  // Open selected journal/entry
  const openJournal = (entry) => {
    setSelectedJournal(entry);
    if (entry.type === 'structured') {
      // Handle structured entry opening - could open in view mode or edit mode
      console.log('Opening structured entry:', entry);
    } else {
      // Handle regular journal opening
      console.log('Opening regular journal:', entry);
    }
  };

  // Open an entry pushed from outside (GoalSidebar Recent Entries pill)
  useEffect(() => {
    if (externalEntry && externalEntry.entry) {
      openJournal(externalEntry.entry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalEntry]);

  // Create new entry
  const createNewEntry = () => {
    setSelectedJournal(null);
    setOpenMonth(null);
  };

  // Delete journal entry
  const deleteJournal = async (journalId, event) => {
    event.stopPropagation();
    
    if (!user || !journalId) return;
    
    try {
      // Try to delete from both regular journals and structured entries
      const journalRef = ref(database, `journals/${user.uid}/${journalId}`);
      const structuredRef = ref(database, `structuredEntries/${user.uid}/${journalId}`);
      
      await Promise.all([
        set(journalRef, null),
        set(structuredRef, null)
      ]);
      
      if (selectedJournal && selectedJournal.id === journalId) {
        setSelectedJournal(null);
      }
    } catch (error) {
      console.error('Error deleting journal:', error);
    }
  };

  return (
    <>
      {showCalendar && (
        <MonthRibbon
          currentDate={currentDate}
          savedJournals={getAllEntriesForMonth}
          getJournalsForMonthByDate={getEntriesForMonthByDate}
          openMonth={openMonth}
          setOpenMonth={setOpenMonth}
          createNewEntry={createNewEntry}
          openJournal={openJournal}
          deleteJournal={deleteJournal}
        />
      )}

      <div className={`glass-editor-container ${showCalendar ? 'with-calendar' : 'without-calendar'}`}>
        <div className={`glass-main-layout ${showAIChat ? 'with-ai-chat' : ''} ${leftSidebar ? (leftSidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed') : ''}`}>
          {leftSidebar}
          <StructuredJournalForms
            currentDate={currentDate}
            onSave={saveStructuredEntry}
            initialData={selectedJournal}
            showAIChat={showAIChat}
            handleBusinessCoachToggle={handleBusinessCoachToggle}
          />

          {reflection && (
            <MentorCard
              state={reflection.status === 'pending' ? 'loading' : 'error'}
              headline="Your mentor is reflecting…"
              errorText="Your mentor couldn't reflect on this entry"
              onRetry={() => requestEntryReflection(reflection.entryId, reflection.entryData)}
            />
          )}

          <AIChatPanel
            showAIChat={showAIChat}
            chatMessages={chatMessages}
            isTyping={isTyping}
            chatInput={chatInput}
            setChatInput={setChatInput}
            sendChatMessage={sendChatMessage}
            handleChatKeyPress={handleChatKeyPress}
            chatEndRef={chatEndRef}
            onRetryMessage={() => {
              if (!lastFailedInput) return;
              setChatMessages(prev => prev.filter(m => m.role !== 'error'));
              sendChatMessage(lastFailedInput);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default GlassEditor;